// NPM
import React, { useMemo, useState } from "react";
import _ from "lodash";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, registerables } from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";

// State
import { useSelector } from "react-redux";

// Components
import Tooltip from "./Tooltip";

// Constants
ChartJS.register(...registerables);
ChartJS.register(annotationPlugin);

function formatDate(date) {
  return new Date(date).toLocaleDateString("en-CA", {
    timeZone: "UTC",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function LineGraph(props) {
  // State
  const parties = useSelector((state) => state.parties.value);
  const partyOrder = useSelector((state) => state.partyOrder.value);
  const responsiveView = useSelector((state) => state.responsiveView.value);
  const [tooltipData, setTooltipData] = useState({});

  // Functions
  const handleCustomTooltip = (tooltipModel) => {
    const { opacity, x, y, title, dataPoints, caretX, xAlign } =
      tooltipModel.tooltip;

    if (tooltipModel.replay) {
      return;
    }

    const tooltipDatapoints = _.chain(dataPoints)
      .filter((dp) => dp.label === title[0])
      .sortBy((dp) => partyOrder.indexOf(dp.dataset.label))
      .map((dp) => {
        return {
          label: dp.dataset.label,
          color: dp.dataset.backgroundColor,
          value: dp.raw,
        };
      })
      .value();

    setTooltipData({
      opacity,
      position: {
        x: xAlign === "right" ? x - 16 : x,
        y,
      },
      title,
      datapoints: tooltipDatapoints,
    });
  };

  let borderWidth = 1;
  let pointRadius = 3;
  let maxTicksLimit = 5;
  let height = 500;
  let legendBoxWidth = 25;
  let legendPadding = 50;
  let legendFontSize = 14;

  switch (responsiveView) {
    case "teeny":
    case "mobile":
    case "tablet":
      borderWidth = 1.5;
      pointRadius = 0;
      maxTicksLimit = 3;
      height = 350;
      legendBoxWidth = 15;
      legendPadding = 25;
      legendFontSize = 14;

      break;
  }

  const constructLabels = (timeSeriesData) => {
    return _.chain(timeSeriesData)
      .sortBy("date")
      .map((data) => {
        return formatDate(data.date);
      })
      .uniq()
      .value();
  };

  const constructDatasets = (parties, timeSeriesData) => {
    return _.chain(timeSeriesData)
      .sortBy("date")
      .groupBy("party")
      .reduce((acc, partyTimeSeriesData) => {
        let partyId = partyTimeSeriesData[0].party;
        let party = parties.find((p) => p.id === partyId);

        if (!party) {
          return acc;
        } else {
          acc.push({
            label: party.short,
            borderColor: party.color,
            backgroundColor: party.color,
            data: _.chain(partyTimeSeriesData)
              .map((td) => parseFloat(td.median) * 100)
              .filter((td) => td)
              .value(),
            borderWidth,
            pointRadius,
          });
        }
        return acc;
      }, [])
      .sortBy((dataset) => partyOrder.indexOf(dataset.label))
      .value();
  };

  const annotations = useMemo(() => {
    return (props.events ?? []).reduce((acc, event) => {
      acc[event.date] = {
        type: "line",
        xMin: formatDate(event.date),
        xMax: formatDate(event.date),
        borderColor: "black",
        borderWidth: 2,
        label: {
          content: event.name,
          display: true,
          position: "end",
          rotation: -90,
          backgroundColor: "rgba(255,255,255,0.5)",
          color: "black",
          xAdjust: -13,
          padding: {
            top: 6,
            bottom: 0,
            left: 6,
            right: 6,
          },
        },
      };
      return acc;
    }, {});
  }, [props.events]);

  const graphOptions = {
    maintainAspectRatio: false,
    interaction: {
      mode: "x",
      intersect: false,
    },
    plugins: {
      tooltip: {
        enabled: false,
        position: "nearest",
        external: handleCustomTooltip,
      },
      legend: {
        position: "bottom",
        labels: {
          boxWidth: legendBoxWidth,
          padding: legendPadding,
          font: {
            size: legendFontSize,
          },
        },
      },
      annotation: {
        annotations,
      },
    },
    scales: {
      y: {
        ticks: {
          callback: function (value, index, ticks) {
            if (index === 0 || index === ticks.length - 1) {
              return `${value}% `;
            }
            return `${value}   `;
          },
        },
      },
      x: {
        ticks: {
          autoSkip: true,
          maxRotation: 0,
          minRotation: 0,
          font: {
            size: 12,
          },
          maxTicksLimit,
        },
      },
    },
  };

  if (_.isBlank(parties)) {
    return null;
  }

  const datasets = constructDatasets(parties, props.timeSeriesData);

  const graphData = {
    labels: constructLabels(props.timeSeriesData),
    datasets,
  };

  const graphPlugins = [
    {
      afterDraw: (chart) => {
        if (chart.tooltip.opacity === 1) {
          const { ctx } = chart;
          const { caretX } = chart.tooltip;
          const topY = chart.scales.y.top;
          const bottomY = chart.scales.y.bottom;

          ctx.save();
          ctx.beginPath();
          ctx.moveTo(caretX, topY - 5);
          ctx.lineTo(caretX, bottomY);
          ctx.lineWidth = 1;
          ctx.strokeStyle = "#999";
          ctx.stroke();
          ctx.restore();
        }
      },
    },
  ];

  return (
    <div
      className="Signal__section-visualization LineGraph__holder"
      style={{ height }}
    >
      <Line options={graphOptions} data={graphData} plugins={graphPlugins} />
      <Tooltip tooltipData={tooltipData} />
    </div>
  );
}

export default LineGraph;
